import { useMemo, useRef } from 'react';
import { useActiveDrag } from '../dnd/DragDropProvider';
import type {
  ItemDragData,
  ItemDropData,
  ReservedFolderType,
} from '../dnd/types';
import { useCombinedDndRef } from '../dnd/useCombinedDndRef';
import { useDraggableNode } from '../dnd/useDraggableNode';
import { useDropTargetNode } from '../dnd/useDropTargetNode';

interface UseSidebarItemDraggableArgs {
  itemId: string;
  kind: ItemDragData['kind'];
  parentId: string | null;
  spaceId: string | null;
  label: string;
  ancestorReservedType?: ReservedFolderType | null;
  disabled?: boolean;
  reservedType?: ReservedFolderType | null;
  dropDisabled?: boolean;
}

export const useSidebarItemDraggable = ({
  itemId,
  kind,
  parentId,
  spaceId,
  label,
  ancestorReservedType = null,
  disabled,
  reservedType = null,
  dropDisabled,
}: UseSidebarItemDraggableArgs) => {
  const nodeRef = useRef<HTMLElement | null>(null);
  const dragData: ItemDragData = useMemo(
    () => ({
      kind,
      itemId,
      parentId,
      spaceId,
      ancestorReservedType,
    }),
    [kind, itemId, parentId, spaceId, ancestorReservedType],
  );
  const dropData: ItemDropData = useMemo(
    () => ({
      kind: 'item-drop',
      itemId,
      itemKind: kind === 'agent' || kind === 'fluxbot' ? 'flow' : kind,
      parentId,
      spaceId,
      reservedType,
      ancestorReservedType,
    }),
    [itemId, kind, parentId, spaceId, reservedType, ancestorReservedType],
  );

  const effectiveDropDisabled =
    dropDisabled || ancestorReservedType !== null || reservedType !== null;

  const activeDrag = useActiveDrag();
  const isFolderActive = activeDrag?.data.kind === 'folder';

  const draggable = useDraggableNode({
    data: dragData,
    label,
    disabled,
  });
  const dropTarget = useDropTargetNode({
    data: dropData,
    mode: 'item',
    disabled: effectiveDropDisabled || isFolderActive,
  });

  const setNodeRef = useCombinedDndRef(
    nodeRef,
    draggable.setRef,
    dropTarget.setRef,
  );

  return {
    setNodeRef,
    isDragging: draggable.isDragging,
    showDropAbove: dropTarget.position === 'above',
    showDropBelow: dropTarget.position === 'below',
  };
};
